import {
  Arrange,
  Container,
  FormField,
  Icon,
  Radio,
  Select,
  Spacer,
  SvgSparkle,
  Text,
} from '@loomhq/lens';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateWelcomeRole, updateWelcomeUseCasePlan } from 'actions/welcome';
import { ONBOARDING_JOB_ROLES_WITH_MESSAGE } from 'constants/employmentRoles';
import {
  COMPLETED_ONBOARDING_STEP,
  SELECTED_USE_CASE,
  USER_WORK_PERSONA_SELECTED,
} from 'constants/events';
import {
  ONBOARDING_SELECTED_USE_CASE,
  ONBOARDING_STEP_COMPLETE,
  ONBOARDING_USER_WORK_PERSONA_SELECTED,
} from 'constants/metrics';
import { PRIMARY_WEB_APP } from 'constants/onboarding';
import { EDUCATION, PERSONAL, WORK } from 'constants/useCasePlans';
import { patchUserPersonaInfoCreator } from 'creators/welcome';
import * as analytics from 'utilities/analytics';
import { incrementMetric } from 'utilities/metrics';

import { PERSONA_USE_CASE_STEP } from '../../constants';
import { DefaultCta, MobileScrollContainer } from '../../cta';
import { PageHeader, PageSubHeader } from '../../headers';

export const USE_CASE_WORK = 'At work';
export const USE_CASE_EDUCATION = 'For education';
export const USE_CASE_PERSONAL = 'For personal use';

export const USE_CASE_OPTIONS = [
  USE_CASE_WORK,
  USE_CASE_EDUCATION,
  USE_CASE_PERSONAL,
];

export const MAP_USE_CASE_TO_CLEARBIT = {
  [USE_CASE_WORK]: WORK,
  [USE_CASE_EDUCATION]: EDUCATION,
  [USE_CASE_PERSONAL]: PERSONAL,
};

export const MAP_CLEARBIT_TO_USE_CASE = {
  [WORK]: USE_CASE_WORK,
  [EDUCATION]: USE_CASE_EDUCATION,
  [PERSONAL]: USE_CASE_PERSONAL,
};

const USE_CASE_MESSAGE = {
  [USE_CASE_EDUCATION]: 'Teachers often use Loom to record lesson plans',
  [USE_CASE_PERSONAL]:
    'People often use Loom to send a video message to friends and family',
};

const JobDescriptionPicker = ({
  currentCompanyRole,
  isSimplifiedOnboarding,
}) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState(null);

  const onChange = e => {
    const selectedOption = ONBOARDING_JOB_ROLES_WITH_MESSAGE.find(
      option => option.role === e.value
    );

    dispatch(updateWelcomeRole(e.value));
    setMessage(selectedOption.message);
  };

  return (
    <Container
      backgroundColor="grey1"
      padding="medium"
      minWidth={{ default: '100%', xsmall: '24rem' }}
      radius="medium"
    >
      <Text
        fontWeight={isSimplifiedOnboarding ? 'book' : 'medium'}
        color="grey8"
      >
        Which role best describes what you do?
      </Text>
      <Spacer bottom="xsmall" />
      <Select
        placeholder="Select your role"
        onChange={onChange}
        selectedOptionValue={currentCompanyRole}
        options={ONBOARDING_JOB_ROLES_WITH_MESSAGE.map(option => {
          return { value: option.role, title: option.role };
        })}
      />
      {message && (
        <Spacer top="small">
          <div className="flex">
            <Spacer right={1} top=".2rem">
              <Icon icon={<SvgSparkle />} color="grey6" size={2} />
            </Spacer>
            <Text color="grey6">{message}</Text>
          </div>
        </Spacer>
      )}
    </Container>
  );
};

const PersonaDetails = ({
  persona,
  currentCompanyRole,
  isSimplifiedOnboarding,
}) => {
  return (
    <Spacer left={3.5} top="small">
      {persona === USE_CASE_WORK ? (
        <JobDescriptionPicker
          isSimplifiedOnboarding={isSimplifiedOnboarding}
          currentCompanyRole={currentCompanyRole}
        />
      ) : (
        <Text color="grey6">{USE_CASE_MESSAGE[persona]}</Text>
      )}
    </Spacer>
  );
};

export const PersonaPicker = ({
  selection,
  setSelection,
  currentCompanyRole,
  isSimplifiedOnboarding = false,
}) => {
  return (
    <Container>
      <Arrange autoFlow="row" gap={2}>
        {USE_CASE_OPTIONS.map(useCase => {
          const isSelected = selection === useCase;

          return (
            <div key={useCase}>
              <FormField direction="row">
                <Radio
                  id={useCase}
                  name={useCase}
                  isChecked={isSelected}
                  value={useCase}
                  onChange={() => {
                    setSelection(useCase);

                    analytics.track(SELECTED_USE_CASE, {
                      use_case: useCase,
                      is_simiplified_onboarding: true,
                      source: PRIMARY_WEB_APP,
                    });

                    incrementMetric(ONBOARDING_SELECTED_USE_CASE, {
                      step: SELECTED_USE_CASE,
                      source: PRIMARY_WEB_APP,
                    });
                  }}
                />
                <label htmlFor={useCase}>
                  <Spacer left="small">
                    <Text size={isSimplifiedOnboarding ? 'large' : 'medium'}>
                      {useCase}
                    </Text>
                  </Spacer>
                </label>
              </FormField>
              {isSelected && (
                <PersonaDetails
                  persona={selection}
                  currentCompanyRole={currentCompanyRole}
                  isSimplifiedOnboarding={isSimplifiedOnboarding}
                />
              )}
            </div>
          );
        })}
      </Arrange>
    </Container>
  );
};

const StreamlinedPersonaUseCase = ({
  nextClickHandler,
  isLastPersonaStep,
  isMobile,
}) => {
  const dispatch = useDispatch();

  const currentUseCasePlan = useSelector(state => state.welcome.use_case_plan);
  const currentCompanyRole = useSelector(state => state.welcome.role);

  const isWorkPersona = currentUseCasePlan === WORK;

  // seed initial selectron based on clearbit/saved state
  const [useCaseSelection, setUseCaseSelection] = useState(
    MAP_CLEARBIT_TO_USE_CASE[currentUseCasePlan]
  );
  const [formIsComplete, setFormIsComplete] = useState(false);

  useEffect(() => {
    dispatch(
      updateWelcomeUseCasePlan(MAP_USE_CASE_TO_CLEARBIT[useCaseSelection])
    );

    if ([USE_CASE_EDUCATION, USE_CASE_PERSONAL].includes(useCaseSelection)) {
      setFormIsComplete(true);
    }

    if (useCaseSelection === USE_CASE_WORK) {
      setFormIsComplete(Boolean(currentCompanyRole));
    }
  }, [dispatch, useCaseSelection, currentCompanyRole]);

  const onSubmit = async () => {
    if (!useCaseSelection) {
      return;
    }

    await dispatch(
      patchUserPersonaInfoCreator({
        use_case_plan: currentUseCasePlan,
        use_case_plan_persona: useCaseSelection,
        persona_role: isWorkPersona ? currentCompanyRole : null,
        complete: isLastPersonaStep,
      })
    );

    dispatch(
      updateWelcomeUseCasePlan(MAP_USE_CASE_TO_CLEARBIT[useCaseSelection])
    );

    analytics.track(COMPLETED_ONBOARDING_STEP, {
      step: PERSONA_USE_CASE_STEP,
      use_case_plan: currentUseCasePlan,
      use_case_plan_persona: useCaseSelection,
      persona_role: isWorkPersona ? currentCompanyRole : null,
      is_simiplified_onboarding: true,
      source: PRIMARY_WEB_APP,
    });

    incrementMetric(ONBOARDING_STEP_COMPLETE, {
      step: PERSONA_USE_CASE_STEP,
      source: PRIMARY_WEB_APP,
    });

    if (currentUseCasePlan === WORK) {
      analytics.track(USER_WORK_PERSONA_SELECTED, {
        is_simiplified_onboarding: true,
        source: PRIMARY_WEB_APP,
      });

      incrementMetric(ONBOARDING_USER_WORK_PERSONA_SELECTED, {
        source: PRIMARY_WEB_APP,
      });
    }

    nextClickHandler();
  };

  return (
    <Container height="100%">
      <MobileScrollContainer isMobile={isMobile}>
        <Spacer left={1} right={1}>
          <PageHeader text="What do you plan to use Loom for?" />
          <Spacer top="large" bottom={{ default: 'large', medium: 'xlarge' }}>
            <PageSubHeader text="We will provide helpful resources and videos to help you get started." />
          </Spacer>

          <PersonaPicker
            selection={useCaseSelection}
            setSelection={setUseCaseSelection}
            currentCompanyRole={currentCompanyRole}
            isSimplifiedOnboarding={true}
          />
        </Spacer>
      </MobileScrollContainer>

      <DefaultCta
        isMobile={isMobile}
        onClick={onSubmit}
        isDisabled={!formIsComplete}
      />
    </Container>
  );
};

export default StreamlinedPersonaUseCase;
